export const LOGIN_PATH = "/";
export const DEFAULT_AUTHENTICATED_PATH = "/dashboard/";
export const LOGOUT_PATH = "/action/logout";
export const REFRESH_SESSION_PATH = "/action/refresh-session";
export const AUTH_EXPIRED_EVENT = "kmcms:auth-expired";
export const NEW_PASSWORD_REQUIRED_STEP =
	"CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";

export const PASSWORD_PATTERN =
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?\-“!@#%&/,><’:;|_~`])\S{8,99}$/;

const PUBLIC_PATHS = new Set(["/", "/invite", "/confirm"]);

export function isPublicPath(pathname: string) {
	if (PUBLIC_PATHS.has(pathname)) return true;
	return pathname.startsWith("/action/");
}

export function isProtectedPath(pathname: string) {
	return pathname.startsWith("/dashboard") || pathname.startsWith("/oauth");
}

export function safeRedirect(
	to: string | null | undefined,
	defaultTo = DEFAULT_AUTHENTICATED_PATH
) {
	if (!to || !to.startsWith("/") || to.startsWith("//")) {
		return defaultTo;
	}
	if (to.startsWith("/action/")) {
		return defaultTo;
	}
	return to;
}

export function loginPathWithRedirect(currentUrl: string) {
	const url = new URL(currentUrl);
	const next = url.pathname + url.search;
	if (!next || next === "/" || isPublicPath(url.pathname)) {
		return LOGIN_PATH;
	}
	return `${LOGIN_PATH}?redirect=${encodeURIComponent(next)}`;
}

function decodeBase64(value: string) {
	const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
	if (typeof atob === "function") {
		return atob(normalized);
	}
	return Buffer.from(normalized, "base64").toString("utf8");
}

export function decodeJwtPayload(
	token: string
): Record<string, unknown> | null {
	try {
		const payload = token.split(".")[1];
		if (!payload) return null;
		return JSON.parse(decodeBase64(payload)) as Record<string, unknown>;
	} catch {
		return null;
	}
}

export function isJwtExpired(token: string, skewSeconds = 30) {
	const payload = decodeJwtPayload(token);
	if (!payload || typeof payload.exp !== "number") return true;
	return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
}

export function isSessionAuthFailure(message: string, statusCode?: number) {
	const msg = message.toLowerCase();
	if (statusCode === 401) return true;
	if (
		msg.includes("not authorized") ||
		msg.includes("unauthorized") ||
		msg.includes("no current user") ||
		msg.includes("refresh token has expired") ||
		msg.includes("refresh token has been revoked") ||
		msg.includes("token has expired")
	) {
		return true;
	}
	return false;
}

export function notifyAuthExpired() {
	if (typeof window === "undefined") return;
	if (isPublicPath(window.location.pathname)) return;
	window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}
